import React, { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { GameSummary } from '../api/game';
import { useOnboarding } from '../context/OnboardingContext';
import OnboardingTooltip from '../components/OnboardingTooltip';
import '../styles/global.css';

interface LocationState {
  summary: GameSummary;
  categoryId: string;
}

const GameResults: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { shouldShowTooltip, dismissStep } = useOnboarding();
  const state = location.state as LocationState | null;
  const rewardsRef = useRef<HTMLDivElement>(null);

  // Check for pending level-up and redirect
  useEffect(() => {
    if (state?.summary?.pending_level_up && state?.summary?.character_id) {
      // Small delay to show the results first
      const timer = setTimeout(() => {
        navigate(`/level-up/${state.summary.character_id}`, {
          state: { characterId: state.summary.character_id }
        });
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [state?.summary?.pending_level_up, state?.summary?.character_id, navigate]);

  if (!state?.summary) {
    return (
      <div className="page">
        <div className="container">
          <div className="error">No results available</div>
          <button className="btn btn-primary" onClick={() => navigate('/account')} style={{ marginTop: 20 }}>
            Back to Home
          </button>
        </div>
      </div>
    );
  }

  const { summary, categoryId } = state;
  const percentage = Math.round((summary.correct_answers / summary.total_questions) * 100);

  const getPerformanceMessage = () => {
    if (summary.was_perfect) return 'PERFECT!';
    if (percentage >= 90) return 'Excellent!';
    if (percentage >= 70) return 'Great job!';
    if (percentage >= 50) return 'Good effort!';
    return 'Keep practicing!';
  };

  const getPerformanceEmoji = () => {
    if (summary.was_perfect) return '🏆';
    if (percentage >= 90) return '🌟';
    if (percentage >= 70) return '👍';
    if (percentage >= 50) return '💪';
    return '📚';
  };

  const handlePlayAgain = () => {
    navigate(`/game/${categoryId}`);
  };

  const handleBackToHome = () => {
    navigate('/account');
  };

  return (
    <div className="page results-page">
      <div className="results-container">
        <div className="results-card">
          <div className="results-emoji">{getPerformanceEmoji()}</div>
          <h1 className="results-title">{getPerformanceMessage()}</h1>

          <div className="results-score">
            <div className="score-circle">
              <span className="score-number">{summary.correct_answers}</span>
              <span className="score-divider">/</span>
              <span className="score-total">{summary.total_questions}</span>
            </div>
            <div className="score-percentage">{percentage}% correct</div>
          </div>

          <div ref={rewardsRef} className="results-rewards">
            <div className="reward-item">
              <span className="reward-icon">⭐</span>
              <span className="reward-value">+{summary.xp_earned}</span>
              <span className="reward-label">XP</span>
            </div>
            <div className="reward-item">
              <span className="reward-icon">🪙</span>
              <span className="reward-value">+{summary.coins_earned}</span>
              <span className="reward-label">Coins</span>
            </div>
          </div>

          <div className="results-character">
            <div className="character-stats">
              <span className="stat-label">Character XP:</span>
              <span className="stat-value">{summary.character_new_xp}</span>
            </div>
            <div className="character-stats">
              <span className="stat-label">Level:</span>
              <span className="stat-value">{summary.character_level}</span>
            </div>
          </div>

          {summary.was_perfect && (
            <div className="perfect-notification" style={{
              backgroundColor: '#fff3cd',
              border: '2px solid #ffc107',
              borderRadius: '8px',
              padding: '12px',
              marginTop: '16px',
              textAlign: 'center'
            }}>
              <span style={{ fontSize: '1.5em' }}>&#127942;</span>
              <span style={{ marginLeft: '8px', fontWeight: 'bold', color: '#856404' }}>
                Perfect Score!
              </span>
            </div>
          )}

          {summary.unlocked_category_name && (
            <div className="unlock-notification" style={{
              backgroundColor: '#d4edda',
              border: '2px solid #28a745',
              borderRadius: '8px',
              padding: '12px',
              marginTop: '16px',
              textAlign: 'center'
            }}>
              <span style={{ fontSize: '1.5em' }}>&#128275;</span>
              <span style={{ marginLeft: '8px', fontWeight: 'bold', color: '#155724' }}>
                Unlocked: {summary.unlocked_category_name}!
              </span>
            </div>
          )}

          {summary.mastery_achieved && (
            <div className="mastery-notification" style={{
              backgroundColor: '#fff3cd',
              border: '3px solid #FFD700',
              borderRadius: '8px',
              padding: '16px',
              marginTop: '16px',
              textAlign: 'center',
              background: 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)'
            }}>
              <div style={{ fontSize: '2em', marginBottom: '8px' }}>
                &#127942;&#127942;&#127942;
              </div>
              <div style={{ fontWeight: 'bold', color: '#5c4d00', fontSize: '1.2em' }}>
                CATEGORY MASTERED!
              </div>
              <div style={{ color: '#5c4d00', marginTop: '4px' }}>
                Bonus: +{summary.mastery_bonus} XP & Coins!
              </div>
            </div>
          )}

          {summary.pending_level_up && (
            <div className="levelup-notification">
              <span className="levelup-notification-icon">&#11088;</span>
              <span className="levelup-notification-text">Level Up! Redirecting...</span>
            </div>
          )}

          <div className="results-actions">
            <button className="btn btn-primary" onClick={handlePlayAgain}>
              Play Again
            </button>
            <button className="btn btn-secondary" onClick={handleBackToHome}>
              Back to Home
            </button>
          </div>
        </div>
      </div>

      {/* Onboarding tooltip */}
      {shouldShowTooltip('results', 1) && (
        <OnboardingTooltip
          targetRef={rewardsRef}
          title="Nice work!"
          message="You earned XP and coins. Keep playing to level up your character!"
          onDismiss={() => dismissStep('results', 1)}
        />
      )}
    </div>
  );
};

export default GameResults;
